import { useState } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table'
import { Link } from 'react-router-dom'
import { Pencil, Trash2 } from 'lucide-react'
import type { BranchOffice } from '../types'
import { ConfirmDialog } from '@/shared/components/ui/molecules/ConfirmDialog'

const columnHelper = createColumnHelper<BranchOffice>()

interface BranchesTableProps {
  data: BranchOffice[]
  onDelete: (id: number) => void
  isLoading: boolean
}

export function BranchesTable({
  data,
  onDelete,
  isLoading,
}: BranchesTableProps) {
  const [deleteBranch, setDeleteBranch] = useState<BranchOffice | null>(null)

  const columns = [
    columnHelper.accessor('id', { header: 'ID', size: 60 }),
    columnHelper.accessor('commune', { header: 'Comuna' }),
    columnHelper.accessor('address', { header: 'Dirección' }),
    columnHelper.display({
      id: 'actions',
      header: 'Acciones',
      size: 120,
      cell: ({ row }) => (
        <div className="flex gap-2">
          <Link
            to={`/branches/${row.original.id}/edit`}
            className="text-primary-600 hover:text-primary-800 transition-colors"
            title="Editar"
          >
            <Pencil size={18} />
          </Link>
          <button
            onClick={() => setDeleteBranch(row.original)}
            className="text-red-500 hover:text-red-700 transition-colors"
            title="Eliminar"
          >
            <Trash2 size={18} />
          </button>
        </div>
      ),
    }),
  ]

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  const handleConfirmDelete = () => {
    if (deleteBranch) {
      onDelete(deleteBranch.id)
      setDeleteBranch(null)
    }
  }

  if (isLoading) {
    return <div className="animate-pulse h-64 bg-gray-100 rounded-lg" />
  }

  return (
    <div>
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left border-collapse">
          <thead className="bg-gray-50 border-b">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id} className="px-4 py-3 font-medium text-gray-700">
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-12 text-center text-gray-500">
                  No se encontraron sucursales
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="border-b hover:bg-gray-50 transition-colors">
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-4 py-3">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Delete confirmation dialog */}
      <ConfirmDialog
        isOpen={!!deleteBranch}
        title="Eliminar Sucursal"
        message={deleteBranch ? `Estas seguro de eliminar la sucursal ${deleteBranch.commune} - ${deleteBranch.address}?` : ''}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteBranch(null)}
      />
    </div>
  )
}
